const { isEmpty, pick, pickBy, mapValues, omit, } = require('lodash');
const dedent = require('dedent');

const { malls } = require('../config');

const { entries } = Object;

const fieldsOf = ({ tenant, senders = [], mall: currentMall }) => {
    return {
      mall: {
        label: 'モール',
        type: 'select',
        // Shopify はアプリのインストールでのみ店舗を作る。手入力で作ると未連携の店舗が残り審査で操作される（2.1.1）。
        options: entries(pick(malls, tenant?.tenantOptionValues)).filter(([k]) => k !== 'shopify' || currentMall === 'shopify').map(([k, v]) => ({ value: k, label: v.label })),
        readOnly: _ => _ === 'shopify',
        validations: {
          required: v => v != null,
        },
      },
      name: {
        type: 'string',
        label: '店舗名',
        validations: {
          required: v => !isEmpty(v),
        },
      },
      isStockSyncEnabled: {
        type: 'boolean',
        label: '在庫連携を有効にする',
        initialValue: true,
        hint: 'OFFにすると、この店舗の在庫をモールへ自動更新しなくなります',
      },
      isOrderSyncEnabled: {
        type: 'boolean',
        label: '受注連携を有効にする',
        initialValue: true,
        hint: 'OFFにすると、この店舗の注文をモールから自動取得しなくなります',
      },
      mercariToken: {
        type: 'password',
        label: 'メルカリ APIトークン',
        hidden: _ => _.mall !== 'mercari',
      },
      yahooClientId: {
        type: 'string',
        label: 'Yahoo Client ID',
        hidden: _ => _.mall !== 'yahoo',
      },
      yahooClientSecret: {
        type: 'password',
        label: 'Yahoo Client Secret',
        hidden: _ => _.mall !== 'yahoo',
      },
      yahooSellerId: {
        type: 'string',
        label: 'Yahoo Seller ID',
        hidden: _ => _.mall !== 'yahoo',
      },
      yahooStoreAccountName: {
        type: 'string',
        label: 'Yahoo ストアアカウント名',
        hidden: _ => _.mall !== 'yahoo',
      },
      yahooPublicKey: {
        type: 'text',
        label: 'Yahoo 公開鍵',
        hidden: _ => _.mall !== 'yahoo',
      },
      yahooPublicKeyVersion: {
        type: 'string',
        label: 'Yahoo 公開鍵バージョン',
        hidden: _ => _.mall !== 'yahoo',
      },
      yahooStartOrderNumber: {
        type: 'string',
        label: 'Yahoo 連携開始注文番号',
        hidden: _ => _.mall !== 'yahoo',
      },
      shopifyShop: {
        type: 'string',
        label: 'Shopify Shop ドメイン',
        hidden: _ => _.mall !== 'shopify',
        hint: 'アプリのインストール時に自動で設定されます。',
        readOnly: _ => true,
        validations: {
          // ストア表示名を誤って入力すると OAuth に進めず受注も同期されないため形式を強制する。
          myshopifyDomain: v => isEmpty(v) || /^[a-zA-Z0-9][a-zA-Z0-9-]*\.myshopify\.com$/.test(v),
        },
      },
      // 公開アプリ移行に伴い手入力を廃止（アプリ共通のapi_key/secretを環境変数で持つ）。
      // 既存カスタムアプリ店舗のデータと shopify(shop) の読み取りは温存するため、UIから隠すのみ。
      shopifyApiKey: {
        type: 'string',
        label: 'Shopify API Key',
        hidden: _ => true,
      },
      shopifyApiSecretKey: {
        type: 'password',
        label: 'Shopify API Secret Key',
        hidden: _ => true,
      },
      // OAuthコールバック（authShopifyCallback）で自動保存するため手入力は不要。UIから隠す。
      shopifyAccessToken: {
        type: 'password',
        label: 'Shopify Access Token',
        hidden: _ => true,
      },
      // expiring offline token 用。callback で自動保存し、ensureValidShopifyToken が
      // 失効間近に refresh して更新する。旧・非有効期限トークンの店舗ではこれらは未設定のまま
      // （refresh_token を持たない＝失効しないためそのまま使う）。すべて UI から隠す。
      shopifyRefreshToken: {
        type: 'password',
        label: 'Shopify Refresh Token',
        hidden: _ => true,
      },
      shopifyTokenExpiresAt: {
        type: 'date',
        label: 'Shopify Access Token 期限',
        hidden: _ => true,
      },
      shopifyRefreshTokenExpiresAt: {
        type: 'date',
        label: 'Shopify Refresh Token 期限',
        hidden: _ => true,
      },
      // refresh_token が無効になり再認可が必要になった時刻。
      // Shopify 側は「インストール済み」に見えるのに API が全て 401 になる状態を、
      // 店舗設定画面に表示して再インストールへ誘導するために使う。
      shopifyReauthRequiredAt: {
        type: 'date',
        label: 'Shopify 再認可が必要になった日時',
        hidden: _ => true,
      },
      shopifyStartOrderNumber: {
        type: 'string',
        label: 'Shopify 連携開始注文番号',
        hidden: _ => _.mall !== 'shopify',
        hint: '#1001であれば「1001」と入力',
      },
      shopifyDeliveryDateKey: {
        type: 'string',
        label: 'Shopify 配送マネージャー 日付指定属性名',
        hidden: _ => _.mall !== 'shopify',
      },
      shopifyDeliveryTimeKey: {
        type: 'string',
        label: 'Shopify 配送マネージャー 時間帯指定属性名',
        hidden: _ => _.mall !== 'shopify',
      },
      shopifyDeliveryDateFormat: {
        type: 'select',
        label: 'Shopify 配送マネージャー 日付指定フォーマット指定',
        options: ['yyyy/mm/dd (DD)', 'yyyy/mm/dd', 'yyyy-mm-dd'].map(_ => ({ value: _, label: _, })),
        hidden: _ => _.mall !== 'shopify',
      },
      // TikTok Shopの注文IDは連番ではないため、他モールの「連携開始注文番号」に相当する
      // 取り込み開始条件を日時で持つ。未設定の間は受注を取り込まない。
      mercariStartOrderNumber: {
        type: 'string',
        label: 'メルカリ 連携開始注文番号',
        hidden: _ => _.mall !== 'mercari',
        hint: 'この注文番号以降の注文のみ取り込みます。未設定の間は受注連携を行いません。',
      },
      // メルカリ側で発送完了メッセージが自動送信されるため、設定・送信処理ともに停止している
      mercariShippedMessageBody: {
        type: 'text',
        label: 'メルカリ 発送完了メッセージ',
        rows: 8,
        hidden: _ => true,
        hint: '発送完了時に取引メッセージとして購入者に送信します。未設定の場合は送信しません。{{ORDERER_NAME}} {{CARRIER_NAME}} {{TRACKING_NUMBER}} {{SHIPPED_DATE}} が使えます。',
      },
      qoo10ApiKey: {
        type: 'password',
        label: 'Qoo10 API Key',
        hidden: _ => _.mall !== 'qoo10',
        hint: 'QSMの「Qoo10へのお問い合わせ」からカテゴリー[システム]>[API・FTP・QSM権限]で申請して発行されるキーです。1年ごとに更新が必要です。',
      },
      // Qoo10の注文番号は連番ではないため、取り込み開始条件を日時で持つ。
      qoo10StartOrderedAt: {
        type: 'date',
        label: 'Qoo10 連携開始日時',
        hidden: _ => _.mall !== 'qoo10',
        hint: 'この日時以降に注文されたもののみ取り込みます。未設定の間は受注連携を行いません。',
      },
      ttsStartOrderedAt: {
        type: 'date',
        label: 'TikTok Shop 連携開始日時',
        hidden: _ => _.mall !== 'tts',
        hint: 'この日時以降に作成された注文のみ取り込みます。未設定の間は受注連携を行いません。',
      },
      rakutenServiceSecret: {
        type: 'password',
        label: '楽天 Service Secret',
        validations: {
          required: v => !isEmpty(v),
        },
        hidden: _ => _.mall !== 'rakuten',
      },
      rakutenLicenseKey: {
        type: 'password',
        label: '楽天 License Key',
        validations: {
          required: v => !isEmpty(v),
        },
        hidden: _ => _.mall !== 'rakuten',
      },
      rakutenShopUrl: {
        type: 'string',
        label: '楽天 Shop URL',
        validations: {
          required: v => !isEmpty(v),
        },
        hidden: _ => _.mall !== 'rakuten',
      },
      rakutenStartOrderNumber: {
        type: 'string',
        label: '楽天 連携開始注文番号',
        hidden: _ => _.mall !== 'rakuten',
      },
      senderId: {
        type: 'select',
        label: '配送元',
        options: senders.map(_ => ({ value: _.id, label: `${_.code} - ${_.name}` })),
        hint: '未設定の場合はテナントのデフォルト配送元が使用されます',
      },
      rakutenMailFromName: {
        type: 'string',
        label: '楽天メール 送信者名',
        validations: {
          required: v => !isEmpty(v),
        },
        hidden: _ => _.mall !== 'rakuten',
        group: 'mail',
      },
      rakutenMailFromAddress: {
        type: 'string',
        label: '楽天メール 送信者アドレス',
        validations: {
          required: v => !isEmpty(v),
        },
        hidden: _ => _.mall !== 'rakuten',
        group: 'mail',
      },
      rakutenMailHost: {
        type: 'string',
        label: '楽天メール ホスト名',
        validations: {
          required: v => !isEmpty(v),
        },
        hidden: _ => _.mall !== 'rakuten',
        group: 'mail',
      },
      rakutenMailPort: {
        type: 'string',
        label: '楽天メール ポート番号',
        validations: {
          required: v => !isEmpty(v),
        },
        hidden: _ => _.mall !== 'rakuten',
        group: 'mail',
      },
      rakutenMailUserName: {
        type: 'string',
        label: '楽天メール ユーザー名',
        validations: {
          required: v => !isEmpty(v),
        },
        hidden: _ => _.mall !== 'rakuten',
        group: 'mail',
      },
      rakutenMailPassword: {
        type: 'password',
        label: '楽天メール パスワード',
        validations: {
          required: v => !isEmpty(v),
        },
        hidden: _ => _.mall !== 'rakuten',
        group: 'mail',
      },
      rakutenMailShippedMailTitle: {
        type: 'string',
        label: '楽天メール 発送完了メールタイトル',
        validations: {
          required: v => !isEmpty(v),
        },
        placeholder: '発送完了のお知らせ',
        hidden: _ => _.mall !== 'rakuten',
        group: 'mail',
      },
      rakutenMailShippedMailBody: {
        type: 'text',
        label: '楽天メール 発送完了メール本文',
        validations: {
          required: v => !isEmpty(v),
        },
        rows: 10,
        hint: dedent`
          {{ORDERER_NAME}} 注文者名
          {{TRACKING_NUMBER}} 伝票番号
          {{CARRIER_NAME}} 配送会社名
          {{SHIPPED_DATE}} 発送日
          {{DESTINATION_POSTAL_CODE}} 送付先郵便番号
          {{DESTINATION_ADDRESS}} 送付先住所
          {{DESTINATION_NAME}} 送付先名
        `,
        hidden: _ => _.mall !== 'rakuten',
        group: 'mail',
      },
      rakutenMailThanksMailTitle: {
        type: 'string',
        label: '楽天メール サンクスメールタイトル',
        placeholder: 'ご注文ありがとうございます',
        hint: '受注時に自動送信されます。タイトル・本文の両方が設定されている場合のみ送信します。',
        hidden: _ => _.mall !== 'rakuten',
        group: 'mail',
      },
      rakutenMailThanksMailBody: {
        type: 'text',
        label: '楽天メール サンクスメール本文',
        rows: 10,
        hint: dedent`
          {{ORDERER_NAME}} 注文者名
          {{ORDER_NUMBER}} 注文番号
          {{ORDER_DATE}} 注文日
        `,
        hidden: _ => _.mall !== 'rakuten',
        group: 'mail',
      },
      yahooMailFromName: {
        type: 'string',
        label: 'Yahooメール 送信者名',
        hidden: _ => _.mall !== 'yahoo',
        group: 'mail',
      },
      yahooMailFromAddress: {
        type: 'string',
        label: 'Yahooメール 送信者アドレス',
        hint: 'サンクスメールのFromに使うメールアドレスです。保存後、「メールドメイン認証」から別途ドメイン認証を行う必要があります（未認証の間は既定の送信元で送信されます）。',
        hidden: _ => _.mall !== 'yahoo',
        group: 'mail',
      },
      yahooMailThanksMailTitle: {
        type: 'string',
        label: 'Yahooメール サンクスメールタイトル',
        placeholder: 'ご注文ありがとうございます',
        hint: '受注時に自動送信されます。タイトル・本文の両方が設定されている場合のみ送信します。',
        hidden: _ => _.mall !== 'yahoo',
        group: 'mail',
      },
      yahooMailThanksMailBody: {
        type: 'text',
        label: 'Yahooメール サンクスメール本文',
        rows: 10,
        hint: dedent`
          {{ORDERER_NAME}} 注文者名
          {{ORDER_NUMBER}} 注文番号
          {{ORDER_DATE}} 注文日
        `,
        hidden: _ => _.mall !== 'yahoo',
        group: 'mail',
      },
      yahooMailShippedMailTitle: {
        type: 'string',
        label: 'Yahooメール 発送完了メールタイトル',
        placeholder: '発送完了のお知らせ',
        hint: '出荷確定時に自動送信されます。タイトル・本文の両方が設定されている場合のみ送信します。',
        hidden: _ => _.mall !== 'yahoo',
        group: 'mail',
      },
      yahooMailShippedMailBody: {
        type: 'text',
        label: 'Yahooメール 発送完了メール本文',
        rows: 10,
        hint: dedent`
          {{ORDERER_NAME}} 注文者名
          {{TRACKING_NUMBER}} 伝票番号
          {{CARRIER_NAME}} 配送会社名
          {{SHIPPED_DATE}} 発送日
          {{DESTINATION_POSTAL_CODE}} 送付先郵便番号
          {{DESTINATION_ADDRESS}} 送付先住所
          {{DESTINATION_NAME}} 送付先名
        `,
        hidden: _ => _.mall !== 'yahoo',
        group: 'mail',
      },
    };
};

module.exports = {
  fields: fieldsOf,
  generalFields: (args) => pickBy(fieldsOf(args), _ => _.group !== 'mail'),
  // メールタブは対象 mall の店舗でのみ表示されるため、その mall 用フィールドだけを返す。
  // mall フィールドを含まないフォーム単独で扱えるよう hidden を取り除く。
  mailFields: ({ mall, ...args }) => mapValues(
    pickBy(fieldsOf(args), _ => _.group === 'mail' && (mall == null || _.hidden == null || !_.hidden({ mall }))),
    _ => omit(_, 'hidden'),
  ),
};

